import OrgIcon from '@/assets/org.svg?react';
import { useOrgContext } from '@/contexts/orgContext';
import { MyOrg, RoleType, UserRole } from '@/lib/models';
import { GroupDiff, TreeDiff } from '@/pages/admin/user/hook';
import {
  User,
  calculateRoleForBilling,
  calculateRoleForGroup,
  calculateRoleForSpace,
  isBillingAdmin,
  isBillingViewer,
  isGroupAdmin,
  isOrgAdmin,
  isSpaceAdmin,
  isSpaceUser,
} from '@/pages/admin/user/type';
import { useQueryGetGroups } from '@/pages/workgroup/hook';
import { WorkGroupT, WorkspaceT } from '@/pages/workgroup/type';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Select } from '@tigergraph/app-ui-lib/select';
import { Body2 } from '@tigergraph/app-ui-lib/typography';
import { SelectOverrides } from 'baseui/select';
import { TreeLabelInteractable, TreeNode, TreeView, toggleIsExpanded } from 'baseui/tree-view';
import { useEffect, useMemo, useState } from 'react';
import { BillIcon, GroupIcon, SpaceIcon } from '../../home/icons';
import mediaQuery from '@/lib/breakpoints';

export type Props = {
  user: User;
  onFormChanged: (diff: TreeDiff) => void;
  hideBilling?: boolean;
};

function calculateGroupRoles(groups: WorkGroupT[] | undefined, user: User) {
  const roles = user.userInfo.roles;
  let ret: WorkGroupT[] = [];
  if (!groups) {
    return ret;
  }
  for (let group of groups) {
    const role = calculateRoleForGroup(roles, group.workgroup_id);
    const g = {
      ...group,
      role,
      workspaces: [] as WorkspaceT[],
    };
    for (let space of group.workspaces) {
      const role = calculateRoleForSpace(roles, group.workgroup_id, space.workspace_id);
      g.workspaces.push({
        ...space,
        role,
      });
    }
    ret.push(g);
  }
  return ret;
}

function changeOrgRole(
  groups: WorkGroupT[] | undefined,
  user: User,
  userBefore: User,
  groupsWithRolesBefore: WorkGroupT[]
) {
  const roles = user.userInfo.roles;
  const isAdminBefore = isOrgAdmin(userBefore.userInfo.roles);

  // swith to org admin
  if (isOrgAdmin(roles)) {
    const groupWithRoles = calculateGroupRoles(groups, user);
    return {
      groupWithRoles,
      billingRole: 'billing-admins' as RoleType,
    };
  }

  const billingRole: RoleType = isBillingAdmin(userBefore.roles)
    ? 'billing-admins'
    : isBillingViewer(userBefore.roles)
    ? 'billing-viewers'
    : 'users';

  let ret: WorkGroupT[] = [];
  if (!groups) {
    return {
      groupWithRoles: ret,
      billingRole,
    };
  }
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const role: RoleType = isAdminBefore ? 'users' : groupsWithRolesBefore[i].role;
    const g = {
      ...group,
      role,
      workspaces: [] as WorkspaceT[],
    };
    for (let j = 0; j < group.workspaces.length; j++) {
      const space = group.workspaces[j];
      const role: RoleType = isAdminBefore ? 'users' : groupsWithRolesBefore[i].workspaces[j].role;
      g.workspaces.push({
        ...space,
        role,
      });
    }
    ret.push(g);
  }
  return {
    groupWithRoles: ret,
    billingRole,
  };
}

function changeGroupRole(group: WorkGroupT, index: number, groupsWithRolesBefore: WorkGroupT[]): WorkGroupT {
  if (group.role === 'workgroup-admins') {
    return {
      ...group,
      workspaces: group.workspaces.map((w) => ({
        ...w,
        role: 'workspace-admins',
      })),
    };
  }

  const isAdminBefore = groupsWithRolesBefore[index].role === 'workgroup-admins';
  const spaces: WorkspaceT[] = [];
  for (let j = 0; j < group.workspaces.length; j++) {
    const space = group.workspaces[j];
    const role: RoleType = isAdminBefore ? 'users' : groupsWithRolesBefore[index].workspaces[j].role;
    spaces.push({
      ...space,
      role,
    });
  }

  return {
    ...group,
    workspaces: spaces,
  };
}

function diffTree(
  groups: WorkGroupT[],
  groupsWithRolesBefore: WorkGroupT[],
  billingRole: RoleType,
  billingRoleBefore: RoleType,
  user: User,
  userBefore: User
) {
  const role: RoleType = isOrgAdmin(user.userInfo.roles) ? 'super-admins' : 'users';
  const roleBefore: RoleType = isOrgAdmin(userBefore.userInfo.roles) ? 'super-admins' : 'users';

  const ret: TreeDiff = {
    orgRole: undefined,
    revokeBillingRole: false,
    assignBillingRole: undefined,
    groups: new Map<string, GroupDiff>(),
  };

  if (role === 'super-admins') {
    if (roleBefore === 'super-admins') {
      // super admin is special, just return
      return ret;
    }

    ret.orgRole = role;
    return ret;
  }

  if (role === 'users') {
    if (roleBefore === 'super-admins') {
      ret.orgRole = role;
    }
  }

  const isBillingAdminRole = billingRole === 'billing-admins';
  const isBillingViewerRole = billingRole === 'billing-viewers';
  const isBillingAdminRoleBefore = isBillingAdmin(userBefore.roles);
  const isBillingViewerRoleBefore = isBillingViewer(userBefore.roles);
  if (billingRole === 'users' && (isBillingAdminRoleBefore || isBillingViewerRoleBefore)) {
    ret.revokeBillingRole = true;
  } else if (isBillingAdminRole && !isBillingAdminRoleBefore) {
    ret.assignBillingRole = 'billing-admins';
  } else if (isBillingViewerRole && !isBillingViewerRoleBefore) {
    ret.assignBillingRole = 'billing-viewers';
  }

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const groupBefore = groupsWithRolesBefore[i];
    if (!groupBefore) {
      continue;
    }

    // 1. for display role, use role in groups
    // 2. when diff role for api, calculate role
    const isGroupAdminBefore = isGroupAdmin(user.userInfo.roles, groupBefore.workgroup_id);

    // is is workgroup admin
    if (group.role === 'workgroup-admins') {
      if (isGroupAdminBefore) {
        continue;
      }

      ret.groups.set(group.workgroup_id, {
        groupRole: group.role,
        adminSpaces: [],
        userSpaces: [],
        revokedSpaces: [],
      });
      continue;
    }

    if (group.role === 'users') {
      if (isGroupAdminBefore) {
        ret.groups.set(group.workgroup_id, {
          groupRole: group.role,
          adminSpaces: [],
          userSpaces: [],
          revokedSpaces: [],
        });
      }
    }

    for (let j = 0; j < group.workspaces.length; j++) {
      const space = group.workspaces[j];
      const spaceBefore = groupBefore.workspaces[j];
      if (!spaceBefore) {
        continue;
      }

      const isSpaceAdminBefore = isSpaceAdmin(user.userInfo.roles, spaceBefore.workgroup_id, spaceBefore.workspace_id);
      const isSpaceUserBefore = isSpaceUser(user.userInfo.roles, spaceBefore.workgroup_id, spaceBefore.workspace_id);

      const diff = ret.groups.get(group.workgroup_id) || {
        groupRole: undefined,
        adminSpaces: [],
        userSpaces: [],
        revokedSpaces: [],
      };

      if (space.role === 'workspace-admins') {
        if (isSpaceAdminBefore) {
          continue;
        }
        diff.adminSpaces.push(space.workspace_id);
      } else if (space.role === 'workspace-users') {
        if (isSpaceUserBefore) {
          continue;
        }
        diff.userSpaces.push(space.workspace_id);
      } else if (space.role === 'users') {
        if (!isSpaceAdminBefore && !isSpaceUserBefore) {
          continue;
        }
        diff.revokedSpaces.push(space.workspace_id);
      }

      ret.groups.set(group.workgroup_id, diff);
    }
  }

  return ret;
}

export default function UserRoleForm({ user, onFormChanged, hideBilling }: Props) {
  const [, theme] = useStyletron();

  const { currentOrg } = useOrgContext();
  const { data } = useQueryGetGroups();
  const groups = data?.Result;
  const groupsWithRolesBefore = useMemo(() => {
    return calculateGroupRoles(groups, user);
  }, [groups, user]);
  const [groupWithRoles, setGroupWithRoles] = useState<WorkGroupT[]>([]);

  const billingRoleBefore = calculateRoleForBilling(user.roles);
  const [billingRole, setBillingRole] = useState<RoleType>(billingRoleBefore);
  const [editUser, setEditUser] = useState(user);
  const role: RoleType = isOrgAdmin(editUser.userInfo.roles) ? 'super-admins' : 'users';

  // this tree data only use to track expand status
  const [toggledTreeData, setToggledTreeData] = useState<TreeNode[]>([]);

  useEffect(() => {
    const diff = diffTree(groupWithRoles, groupsWithRolesBefore, billingRole, billingRoleBefore, editUser, user);
    onFormChanged(diff);
  }, [groupWithRoles, groupsWithRolesBefore, billingRole, billingRoleBefore, editUser, user, onFormChanged]);

  useEffect(() => {
    if (groupsWithRolesBefore) {
      setGroupWithRoles(groupsWithRolesBefore);

      const initialData: TreeNode[] = [
        {
          label: '',
          isExpanded: true,
          id: user.userId,
          children: groupsWithRolesBefore.map((group) => ({
            id: group.workgroup_id,
            label: '',
            isExpanded: true,
            children: group.workspaces.map((space) => ({
              id: space.workspace_id,
              label: '',
            })),
          })),
        },
      ];

      setToggledTreeData(initialData);
    }
  }, [groupsWithRolesBefore, user.userId]);

  // generate real tree data based on
  // 1. toggledTreeData
  // 2. user & role
  const treeData = useMemo(() => {
    const findNode = (node: TreeNode, id: string): TreeNode | undefined => {
      if (node.id === id) {
        return node;
      }
      if (node.children) {
        for (let child of node.children) {
          const ret = findNode(child, id);
          if (ret) {
            return ret;
          }
        }
      }
      return undefined;
    };

    const getExpand = (id: string) => {
      for (let node of toggledTreeData) {
        const ret = findNode(node, id);
        if (ret) {
          return ret.isExpanded;
        }
      }
      return false;
    };

    const billingChildren = {
      id: 'billing',
      isExpanded: false,
      info: {
        billingRole,
      },
      label: (node: TreeNode<any>) => (
        <BillingTreeLabel
          node={node}
          disabled={isOrgAdmin(editUser.userInfo.roles)}
          onChangeRoleType={(newRole) => {
            setBillingRole(newRole);
          }}
        />
      ),
    };

    const workgroupChildren = groupWithRoles.map((group, i) => ({
      id: group.workgroup_id,
      isExpanded: getExpand(group.workgroup_id),
      info: {
        group,
      },
      label: (node: TreeNode<any>) => (
        <WorkgroupTreeLabel
          node={node}
          disabled={isOrgAdmin(editUser.userInfo.roles)}
          onChangeRoleType={(newRole) => {
            let newGroup = {
              ...group,
              role: newRole,
            };

            newGroup = changeGroupRole(newGroup, i, groupsWithRolesBefore);

            setGroupWithRoles([...groupWithRoles.slice(0, i), newGroup, ...groupWithRoles.slice(i + 1)]);
          }}
        />
      ),
      children: group.workspaces.map((space, j) => ({
        id: space.workspace_id,
        isExpanded: getExpand(space.workspace_id),
        info: {
          group,
          space,
        },
        label: (node: TreeNode<any>) => (
          <WorkspaceLabel
            node={node}
            disabled={group.role === 'workgroup-admins'}
            onChangeRoleType={(newRole) => {
              const newGroup: WorkGroupT = {
                ...group,
                workspaces: [
                  ...group.workspaces.slice(0, j),
                  {
                    ...space,
                    role: newRole,
                  },
                  ...group.workspaces.slice(j + 1),
                ],
              };
              setGroupWithRoles([...groupWithRoles.slice(0, i), newGroup, ...groupWithRoles.slice(i + 1)]);
            }}
          />
        ),
      })),
    }));

    const initialData: TreeNode[] = [
      {
        label: (node) => (
          <OrgTreeLabel
            node={node}
            onChangeRoleType={(role) => {
              let newUser = editUser;
              if (role === 'users') {
                newUser = {
                  ...editUser,
                  userInfo: {
                    ...editUser.userInfo,
                    roles: editUser.userInfo.roles.filter((r) => r.name !== 'super-admins'),
                  },
                };
              } else {
                const superUser = {
                  name: 'super-admins',
                } as UserRole;
                newUser = {
                  ...editUser,
                  userInfo: {
                    ...editUser.userInfo,
                    roles: editUser.userInfo.roles.concat(superUser),
                  },
                };
              }
              setEditUser(newUser);

              const { groupWithRoles, billingRole } = changeOrgRole(groups, newUser, user, groupsWithRolesBefore);
              setGroupWithRoles(groupWithRoles);
              setBillingRole(billingRole);
            }}
          />
        ),
        isExpanded: getExpand(user.userId),
        info: {
          role,
          currentOrg,
        },
        id: user.userId,
        children: !hideBilling ? [billingChildren, ...workgroupChildren] : [...workgroupChildren],
      },
    ];
    return initialData;
  }, [
    toggledTreeData,
    user,
    editUser,
    groupWithRoles,
    role,
    currentOrg,
    groups,
    groupsWithRolesBefore,
    billingRole,
    hideBilling,
  ]);

  return (
    <TreeView
      data={treeData}
      onToggle={(node) => setToggledTreeData((prevData) => toggleIsExpanded(prevData, node))}
      overrides={{
        IconContainer: {
          style: {
            color: theme.colors['icon.primary'],
          },
        },
        TreeItemList: {
          style: {
            [mediaQuery.xsmall]: {
              marginLeft: '0px',
            },
          },
        },
      }}
    />
  );
}

type LabelProps = {
  node: TreeNode;
  onChangeRoleType: (role: RoleType) => void;
  disabled?: boolean;
};

const OrgTreeLabel = ({ node, onChangeRoleType }: LabelProps) => {
  const [css] = useStyletron();
  const { currentOrg, role } = node.info as { user: User; currentOrg: MyOrg; role: RoleType };
  return (
    <TreeLabelInteractable>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flex: 1,
        })}
      >
        <Body2 $style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <OrgIcon />
          {currentOrg.org_name}
        </Body2>
        <Select
          size="mini"
          clearable={false}
          searchable={false}
          value={[{ id: role }]}
          options={[
            {
              id: 'super-admins',
              label: 'Org Admin',
            },
            {
              id: 'users',
              label: 'Org Member',
            },
          ]}
          onChange={(params) => onChangeRoleType(params.value[0].id as RoleType)}
          overrides={selectOverrides}
        />
      </div>
    </TreeLabelInteractable>
  );
};

const BillingTreeLabel = ({ node, onChangeRoleType, disabled }: LabelProps) => {
  const [css] = useStyletron();
  const { billingRole } = node.info as { billingRole: RoleType };
  return (
    <TreeLabelInteractable>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flex: 1,
        })}
      >
        <Body2 $style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BillIcon />
          Billing
        </Body2>
        <Select
          disabled={disabled}
          size="mini"
          clearable={false}
          searchable={false}
          value={[{ id: billingRole }]}
          options={[
            {
              id: 'billing-admins',
              label: 'Billing Admin',
            },
            {
              id: 'billing-viewers',
              label: 'Billing Viewer',
            },
            {
              id: 'users',
              label: 'Not Specified',
            },
          ]}
          onChange={(params) => onChangeRoleType(params.value[0].id as RoleType)}
          overrides={selectOverrides}
        />
      </div>
    </TreeLabelInteractable>
  );
};

const WorkgroupTreeLabel = ({ node, onChangeRoleType, disabled }: LabelProps) => {
  const [css] = useStyletron();
  const { group } = node.info as { group: WorkGroupT };
  return (
    <TreeLabelInteractable>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flex: 1,
        })}
      >
        <Body2 $style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <GroupIcon />
          {group.name}
        </Body2>
        <Select
          disabled={disabled}
          size="mini"
          clearable={false}
          searchable={false}
          value={[{ id: group.role }]}
          options={[
            {
              id: 'workgroup-admins',
              label: 'Workgroup Admin',
            },
            {
              id: 'users',
              label: 'Not Specified',
            },
          ]}
          onChange={(params) => onChangeRoleType(params.value[0].id as RoleType)}
          overrides={selectOverrides}
        />
      </div>
    </TreeLabelInteractable>
  );
};

const WorkspaceLabel = ({ node, onChangeRoleType, disabled }: LabelProps) => {
  const [css] = useStyletron();
  const { space } = node.info as { group: WorkGroupT; space: WorkspaceT };
  return (
    <TreeLabelInteractable>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flex: 1,
        })}
      >
        <Body2 $style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SpaceIcon />
          {space.name}
        </Body2>
        <Select
          disabled={disabled}
          size="mini"
          clearable={false}
          searchable={false}
          value={[{ id: space.role }]}
          options={[
            {
              id: 'workspace-admins',
              label: 'Workspace Admin',
            },
            {
              id: 'workspace-users',
              label: 'Workspace User',
            },
            {
              id: 'users',
              label: 'Not Specified',
            },
          ]}
          onChange={(params) => onChangeRoleType(params.value[0].id as RoleType)}
          overrides={selectOverrides}
        />
      </div>
    </TreeLabelInteractable>
  );
};

const selectOverrides: SelectOverrides = {
  Root: {
    style: {
      width: '164px',
      [mediaQuery.xsmall]: {
        width: '122px',
      },
    },
  },
  ControlContainer: {
    style: {
      width: '164px',
      [mediaQuery.xsmall]: {
        width: '122px',
      },
    },
  },
  DropdownListItem: {
    style: {
      fontSize: '12px',
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  },
  Popover: {
    props: {
      ignoreBoundary: true,
      popperOptions: {
        modifiers: {
          preventOverflow: {
            enabled: false,
          },
          hide: {
            enabled: false,
          },
        },
      },
    },
  },
};
